export const MOBILE_MAX_LENGTH = 10;
export const MOBILE_REGEX = /^[0-9]{10}$/;

/**
 * Normalize pasted/autofilled Indian mobiles into 10 digits.
 * Strips country-code / trunk prefixes only when they are clearly prefixes:
 *   "+91 6355 054 424" → "6355054424"
 *   "91 6355054424"    → "6355054424"
 *   "916355054424"     → "6355054424"
 *   "06355054424"      → "6355054424"
 * Does NOT strip a real 10-digit number that starts with 91:
 *   "9104659089" stays "9104659089"
 *   typing "91" or "0" while searching stays as typed
 */
export function sanitizeMobileInput(value) {
  let raw = String(value ?? "").trim();

  // Explicit country-code prefixes only (never a bare "91…")
  raw = raw.replace(/^(?:\+|00)91[\s\-.]*/i, "");
  raw = raw.replace(/^91[\s\-.]+/, "");

  let digits = raw.replace(/\D/g, "");

  // Compact 12-digit paste/autofill: "916355054424" — not a 10-digit 91… number
  if (digits.length > MOBILE_MAX_LENGTH && digits.startsWith("91")) {
    digits = digits.slice(2);
  }

  // Trunk 0: "06355054424" → "6355054424"
  // Drop the 0 once a valid Indian start digit (6–9) follows, so typing/paste
  // still works in maxLength=10 fields. A lone "0" in search is kept.
  if (digits.startsWith("0")) {
    const withoutZeros = digits.replace(/^0+/, "");
    if (
      digits.length > MOBILE_MAX_LENGTH ||
      /^[6-9]/.test(withoutZeros)
    ) {
      digits = withoutZeros;
    }
  }

  return digits.slice(0, MOBILE_MAX_LENGTH);
}

export function isValidMobileNumber(value) {
  return MOBILE_REGEX.test(String(value ?? "").trim());
}

export function getMobileValidationError(value, { required = false } = {}) {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return required ? "Mobile number is required" : "";
  if (!isValidMobileNumber(trimmed)) {
    return "Enter a valid 10-digit mobile number";
  }
  return "";
}

export const mobileInputFieldProps = {
  type: "tel",
  inputMode: "numeric",
  maxLength: MOBILE_MAX_LENGTH,
  autoComplete: "tel",
};

export function handleMobileInputChange(event, onValueChange) {
  onValueChange(sanitizeMobileInput(event.target.value));
}
